import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../../../contexts/StoreContext'
import { useAuth } from '../../../contexts/AuthContext'
import { api } from '../../../lib/api'
import { Store as StoreType } from '../../../types'
import {
  ShoppingCart,
  DollarSign,
  Package,
  Users,
  TrendingUp,
  Calendar,
  Store,
  Clock,
  AlertCircle,
  BarChart3,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  Activity,
  Target,
  Zap
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

type Period = 'today' | 'week' | 'month' | 'year' | 'all'

interface PerformanceStats {
  totalOrders: number
  totalRevenue: number
  totalProducts: number
  totalEmployees: number
  pendingOrders: number
  completedOrders: number
  averageOrderValue: number
  revenueChange: number
  ordersChange: number
  storesCount: number
  topStores: Array<{
    id: string
    name: string
    orders: number
    revenue: number
  }>
}

const StoreDashboard = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { stores, loading: storeLoading } = useStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [period, setPeriod] = useState<Period>('month')
  const [stats, setStats] = useState<PerformanceStats | null>(null)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  // Redirigir despachadores a su dashboard
  useEffect(() => {
    if (user?.type === 'dispatcher') {
      const storeId = user.storeId
      if (storeId) {
        navigate(`/store/${storeId}/dispatch`)
      } else {
        navigate('/store/dashboard')
      }
      return
    }
  }, [user, navigate])

  useEffect(() => {
    if (user?.type !== 'store_owner' && user?.type !== 'store_admin') {
      return
    }

    if (!storeLoading && stores.length > 0) {
      loadPerformanceData()
    } else if (!storeLoading && stores.length === 0) {
      setError('No tienes negocios registrados')
      setLoading(false)
    }
  }, [stores, storeLoading, period, user])

  const getDateRange = (period: Period) => {
    const now = new Date()
    let start: Date

    switch (period) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'week':
        start = new Date(now)
        start.setDate(now.getDate() - 7)
        break
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        break
      case 'year':
        start = new Date(now.getFullYear(), 0, 1)
        break
      case 'all':
        start = new Date(0)
        break
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1)
    }

    return { start, end: now }
  }

  const loadPerformanceData = async () => {
    if (stores.length === 0) return

    try {
      setLoading(true)
      setError('')

      const { start, end } = getDateRange(period)
      const storeIds = stores.map((s: StoreType) => s.id)

      const [allOrders, allProducts, allEmployees] = await Promise.all([
        Promise.all(
          storeIds.map((storeId: string) =>
            api.get(`/orders/store/${storeId}?limit=10000`).catch(() => ({ data: { success: true, data: [] } }))
          )
        ),
        Promise.all(
          storeIds.map((storeId: string) =>
            api.get(`/products/store/${storeId}/products`).catch(() => ({ data: { success: true, data: [] } }))
          )
        ),
        Promise.all(
          storeIds.map((storeId: string) =>
            api.get(`/auth/store/${storeId}/employees`).catch(() => ({ data: { success: true, data: [] } }))
          )
        )
      ])

      const orders = allOrders
        .flatMap((response: any) => response.data?.success ? (response.data.data || []) : [])
        .filter((order: any) => {
          const orderDate = new Date(order.created_at)
          return orderDate >= start && orderDate <= end
        })

      const products = allProducts.flatMap(
        (response: any) => response.data?.success ? (response.data.data || []) : []
      )

      const employees = allEmployees.flatMap(
        (response: any) => response.data?.success ? (response.data.data || []) : []
      )

      const previousPeriod = getPreviousPeriod(period)
      const previousOrders = allOrders
        .flatMap((response: any) => response.data?.success ? (response.data.data || []) : [])
        .filter((order: any) => {
          const orderDate = new Date(order.created_at)
          return orderDate >= previousPeriod.start && orderDate < previousPeriod.end
        })

      const totalRevenue = orders.reduce((sum: number, order: any) => {
        return sum + (parseFloat(order.total_amount) + parseFloat(order.delivery_fee || 0))
      }, 0)

      const previousRevenue = previousOrders.reduce((sum: number, order: any) => {
        return sum + (parseFloat(order.total_amount) + parseFloat(order.delivery_fee || 0))
      }, 0)

      const revenueChange = previousRevenue > 0
        ? ((totalRevenue - previousRevenue) / previousRevenue) * 100
        : totalRevenue > 0 ? 100 : 0

      const ordersChange = previousOrders.length > 0
        ? ((orders.length - previousOrders.length) / previousOrders.length) * 100
        : orders.length > 0 ? 100 : 0

      const pendingOrders = orders.filter((order: any) =>
        ['pending', 'confirmed', 'preparing', 'ready', 'delivering'].includes(order.status)
      ).length

      const completedOrders = orders.filter((order: any) =>
        order.status === 'delivered'
      ).length

      const averageOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0

      const storeRevenueMap = new Map<string, { name: string; orders: number; revenue: number }>()
      
      orders.forEach((order: any) => {
        const storeId = order.store_id
        const store = stores.find((s: StoreType) => s.id === storeId)
        if (store) {
          const current = storeRevenueMap.get(storeId) || { name: store.name, orders: 0, revenue: 0 }
          current.orders += 1
          current.revenue += parseFloat(order.total_amount) + parseFloat(order.delivery_fee || 0)
          storeRevenueMap.set(storeId, current)
        }
      })

      const topStores = Array.from(storeRevenueMap.entries())
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5)

      const performanceStats: PerformanceStats = {
        totalOrders: orders.length,
        totalRevenue,
        totalProducts: products.length,
        totalEmployees: employees.filter((e: any) => e.is_active).length,
        pendingOrders,
        completedOrders,
        averageOrderValue,
        revenueChange,
        ordersChange,
        storesCount: stores.length,
        topStores
      }
      
      setStats(performanceStats)
      const cacheKeyForSave = `store_dashboard_stats_${stores.map((s: StoreType) => s.id).join('_')}_${period}`
      setCache(cacheKeyForSave, performanceStats, 2 * 60 * 1000)

    } catch (err: any) {
      console.error('Error loading performance data:', err)
      
      const cacheKeyForError = `store_dashboard_stats_${stores.map((s: StoreType) => s.id).join('_')}_${period}`
      const cachedStatsForError = getCacheStale<PerformanceStats>(cacheKeyForError)
      if ((err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) && cachedStatsForError) {
        showOfflineToast()
        return
      }
      
      setError('Error al cargar datos de rendimiento')
      toast.error('Error al cargar datos de rendimiento')
    } finally {
      setLoading(false)
    }
  }

  const getPreviousPeriod = (currentPeriod: Period) => {
    const now = new Date()
    let start: Date
    let end: Date

    switch (currentPeriod) {
      case 'today':
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        start = new Date(end)
        start.setDate(end.getDate() - 1)
        break
      case 'week':
        end = new Date(now)
        end.setDate(now.getDate() - 7)
        start = new Date(end)
        start.setDate(end.getDate() - 7)
        break
      case 'month':
        end = new Date(now.getFullYear(), now.getMonth(), 1)
        start = new Date(end.getFullYear(), end.getMonth() - 1, 1)
        break
      case 'year':
        end = new Date(now.getFullYear(), 0, 1)
        start = new Date(end.getFullYear() - 1, 0, 1)
        break
      default:
        end = new Date(now)
        start = new Date(0)
    }

    return { start, end }
  }

  const getPeriodLabel = (period: Period) => {
    switch (period) {
      case 'today': return 'Hoy'
      case 'week': return 'Esta Semana'
      case 'month': return 'Este Mes'
      case 'year': return 'Este Año'
      case 'all': return 'Todo el Tiempo'
    }
  }

  if (storeLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-2xl sm:rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando panel de rendimiento...</p>
        </div>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center pb-20">
        <div className="text-center max-w-md px-4">
          <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-8 sm:p-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100/80 rounded-2xl sm:rounded-xl sm:rounded-2xl mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Error al cargar panel</h2>
            <p className="text-gray-600 mb-8 font-medium">{error || 'No se pudieron cargar los datos'}</p>
            <button
              onClick={loadPerformanceData}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl transition-all duration-300 active:scale-95 font-semibold shadow-xl hover:shadow-2xl"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism Style - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                    Panel de Rendimiento
                  </h1>
                  <p className="text-sm text-gray-600 font-semibold">
                    Rendimiento agregado de todos tus negocios
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as Period)}
                  className="px-4 py-2.5 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-sm font-semibold text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer"
                >
                  <option value="today">Hoy</option>
                  <option value="week">Esta Semana</option>
                  <option value="month">Este Mes</option>
                  <option value="year">Este Año</option>
                  <option value="all">Todo el Tiempo</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Period Info */}
        <div className="mb-8">
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 shadow-xl p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">
                  Período Seleccionado
                </p>
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{getPeriodLabel(period)}</p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">
                  Total Negocios
                </p>
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{stats.storesCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Total Orders */}
          <div className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 hover:bg-white/80">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <ShoppingCart className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                {stats.ordersChange !== 0 && (
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl sm:rounded-2xl text-xs font-bold backdrop-blur-sm ${
                    stats.ordersChange > 0 ? 'bg-emerald-50/80 text-emerald-700 border border-emerald-200/50' : 'bg-red-50/80 text-red-700 border border-red-200/50'
                  }`}>
                    {stats.ordersChange > 0 ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                    {Math.abs(stats.ordersChange).toFixed(1)}%
                  </div>
                )}
              </div>
              <dl>
                <dt className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Total Pedidos
                </dt>
                <dd className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                  {stats.totalOrders.toLocaleString()}
                </dd>
                <dd className="text-xs sm:text-sm text-gray-600 font-semibold">
                  {stats.completedOrders} completados • {stats.pendingOrders} pendientes
                </dd>
              </dl>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 hover:bg-white/80">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <DollarSign className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                {stats.revenueChange !== 0 && (
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl sm:rounded-2xl text-xs font-bold backdrop-blur-sm ${
                    stats.revenueChange > 0 ? 'bg-emerald-50/80 text-emerald-700 border border-emerald-200/50' : 'bg-red-50/80 text-red-700 border border-red-200/50'
                  }`}>
                    {stats.revenueChange > 0 ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                    {Math.abs(stats.revenueChange).toFixed(1)}%
                  </div>
                )}
              </div>
              <dl>
                <dt className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Ingresos Totales
                </dt>
                <dd className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-3">
                  RD$ {stats.totalRevenue.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </dd>
                <dd className="text-xs sm:text-sm text-gray-600 font-semibold">
                  Promedio: RD$ {stats.averageOrderValue.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </dd>
              </dl>
            </div>
          </div>

          {/* Total Products */}
          <div className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 hover:bg-white/80">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Package className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
              <dl>
                <dt className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Total Productos
                </dt>
                <dd className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {stats.totalProducts.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>

          {/* Total Employees */}
          <div className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 hover:bg-white/80">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
              <dl>
                <dt className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Total Empleados
                </dt>
                <dd className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {stats.totalEmployees.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* Top Stores */}
        {stats.topStores.length > 0 && (
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center gap-3">
                <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
                <span>Top Negocios por Ingresos</span>
              </h3>
            </div>
            <div className="space-y-4">
              {stats.topStores.map((store, index) => (
                <div
                  key={store.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 sm:p-6 bg-white/40 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl hover:bg-white/60 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0">
                      #{index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Store className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <h4 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{store.name}</h4>
                      </div>
                      <div className="text-sm text-gray-600 font-semibold">
                        {store.orders} pedidos
                      </div>
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">
                      RD$ {store.revenue.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 font-semibold mt-1">
                      {((store.revenue / stats.totalRevenue) * 100).toFixed(1)}% del total
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 shadow-xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl mb-4 shadow-lg">
              <Activity className="w-7 h-7 text-white" />
            </div>
            <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              {stats.completedOrders}
            </div>
            <div className="text-sm font-bold text-gray-700">
              Pedidos Completados
            </div>
          </div>
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 shadow-xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl sm:rounded-2xl mb-4 shadow-lg">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-amber-600 to-amber-700 bg-clip-text text-transparent mb-2">
              {stats.pendingOrders}
            </div>
            <div className="text-sm font-bold text-gray-700">
              Pedidos Pendientes
            </div>
          </div>
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 shadow-xl p-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl sm:rounded-2xl mb-4 shadow-lg">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-2">
              {stats.storesCount}
            </div>
            <div className="text-sm font-bold text-gray-700">
              Negocios Activos
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StoreDashboard
